<template>
  <v-main>
    <app-grid :disabled="!useGrid" :one-column="oneColumn">
      <template v-if="showNavigationAndFooter" #left>
        <custom-navigation v-if="!$vuetify.display.mobile" :max-items-before-fold="5" class="mt-2" />
        <group-list v-if="!$vuetify.display.mobile && configuration.homePageSections.home_my_groups_enabled" />
        <the-footer :show-third-party-software-link="isWithinProfileSettings" />
      </template>

      <template #above-content>
        <home-screen-section v-if="$routerState.isRoot && $vuetify.display.mobile" class="px-4" />
        <div ref="aboveContentContainer" :class="{ 'mt-6': aboveContentPresent && useGrid }">
          <portal-target name="above-content" @change="onAboveContentChange" />
        </div>
      </template>

      <template #content>
        <router-view :name="loggedInAndRedirected ? 'auth' : 'nonAuth'" v-slot="{ Component }" :class="sectionsSpacingClasses">
          <keep-alive :include="['ViewGroups', 'ViewProfile', 'ViewSearch', 'ViewMemberDirectory', 'ViewCourse']">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </template>

      <template #content-secondary>
        <home-screen-section v-if="$routerState.isRoot && !$vuetify.display.mobile" />
        <div :class="sectionsSpacingClasses">
          <portal-target name="content-secondary" />
        </div>
        <template v-if="showSecondaryBanners">
          <desktop-invite-banner v-if="configuration.invitationLinkOnMoreMenu" class="mb-4" />
          <desktop-paywall-nudge-banner class="mb-4" />
        </template>
      </template>
    </app-grid>

    <the-footer v-if="$vuetify.display.mobile && isWithinProfileSettings" class="mt-4 mb-12" show-third-party-software-link />
  </v-main>
</template>

<script>
import { mapState } from "pinia"
import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useConfigurationStore } from "@/store/configuration.js"

import AppGrid from "@/components/AppGrid.vue"
import TheFooter from "@/components/navs/TheFooter.vue"
import DesktopInviteBanner from "@/components/users/DesktopInviteBanner.vue"
import DesktopPaywallNudgeBanner from "@/components/premium/DesktopPaywallNudgeBanner.vue"
import CustomNavigation from "@/components/navs/CustomNavigation.vue"
import HomeScreenSection from "@/components/navs/HomeScreenSection.vue"
import GroupList from "@/components/groups/GroupList.vue"

export default {
  components: {
    AppGrid,
    DesktopPaywallNudgeBanner,
    TheFooter,
    CustomNavigation,
    DesktopInviteBanner,
    HomeScreenSection,
    GroupList
  },
  data() {
    return {
      aboveContentPresent: false,
      contentSecondaryForced: false
    }
  },
  computed: {
    ...mapState(useUserSessionStore, ["isLoggedIn", "newMessagesCount"]),
    ...mapState(useAppStateStore, ["redirectingAfterLogIn", "onboardingModeOrRedirection"]),
    ...mapState(useConfigurationStore, ["configuration"]),
    useGrid() {
      return (
        !this.$vuetify.display.mobile &&
        (this.isLoggedIn || this.$routes.POST === this.$route.name) &&
        !this.onboardingModeOrRedirection &&
        !this.redirectingAfterLogIn &&
        this.$route.name !== this.$routes.PAYWALL &&
        this.$route.name !== this.$routes.LESSON &&
        this.$route.name !== this.$routes.LIVE &&
        this.$route.name !== this.$routes.BROADCAST &&
        this.$route.name !== this.$routes.WALKTHROUGH
      )
    },
    loggedInAndRedirected() {
      return this.isLoggedIn && !this.redirectingAfterLogIn
    },
    oneColumn() {
      return (
        !this.contentSecondaryForced &&
        ([this.$routes.MEMBER_DIRECTORY, this.$routes.MESSAGES, this.$routes.DELETE_ACCOUNT, this.$routes.ARTICLE, this.$routes._404].includes(
          this.$route.name
        ) ||
          this.isWithinProfileSettings)
      )
    },
    isWithinProfileSettings() {
      return [
        this.$routes.PROFILE_SETTINGS,
        this.$routes.PROFILE_SETTINGS_EMAILS,
        this.$routes.PROFILE_SETTINGS_PUSH_NOTIFICATIONS,
        this.$routes.PROFILE_SETTINGS_NOTIFICATIONS_CENTRE
      ].includes(this.$route.name)
    },
    showSecondaryBanners() {
      return (
        ![
          this.$routes.MESSAGES,
          this.$routes.PROFILE,
          this.$routes.PROFILE_EDIT,
          this.$routes.ARTICLE,
          this.$routes.POST,
          this.$routes.COMMENT,
          this.$routes.EVENT,
          this.$routes.SEARCH,
          this.$routes.COURSE
        ].includes(this.$route.name) &&
        this.loggedInAndRedirected &&
        this.useGrid &&
        !this.oneColumn
      )
    },
    showNavigationAndFooter() {
      if (!this.useGrid) {
        return false
      }
      return (this.loggedInAndRedirected && this.$route.name !== this.$routes.WALKTHROUGH) || this.$route.name === this.$routes.POST
    },
    sectionsSpacingClasses() {
      if (this.redirectingAfterLogIn || !this.useGrid) {
        return null
      }

      return this.aboveContentPresent ? "mt-4 mb-1" : "mt-6 mb-1"
    }
  },
  watch: {
    $route() {
      this.contentSecondaryForced = false
    }
  },
  mounted() {
    this.$eventBus.$on("layout-force-content-secondary", this.setForcedTwoColumns)
  },
  beforeUnmount() {
    this.$eventBus.$off("layout-force-content-secondary", this.setForcedTwoColumns)
  },
  methods: {
    setForcedTwoColumns() {
      this.contentSecondaryForced = true
    },
    onAboveContentChange() {
      // Workaround for new vue portal target which @change event doesn't work as before
      this.aboveContentPresent =
        this.$refs.aboveContentContainer.innerHTML &&
        this.$refs.aboveContentContainer.innerHTML !== '<div style="display: none;"></div>' &&
        this.$refs.aboveContentContainer.innerHTML !== "<!---->"
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.content-secondary) {
  overflow-x: hidden;
}
</style>
